import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";
import { memo, useContext } from "react";
import styled, { css } from "styled-components";
import { NewCheckbox } from "../../checkbox/NewCheckbox";
import { HighlightText } from "../../highlightText/HighlightText";
import { defaultControlSize } from "../../newCommon/ControlsCommon";
import { ProductSummary } from "../../productSummary/ProductSummary";
import { cssBody200, cssBody400 } from "../../typography/styles";
import { USelectContext } from "../context/context";
import { FlagLabel, flagsMarketplaceRelation } from "../marketplace/marketplace";
export const SelectOption = memo(({ option, size = defaultControlSize, active, hover, disabled, onClick, onMouseEnter, }) => {
    const { debounceSearch } = useContext(USelectContext);
    return (_jsx(StyledSingleOption, { "$size": size, "$active": active, "$hover": hover, "$disabled": disabled, role: "option", "data-value": option.value, onClick: onClick, onMouseEnter: onMouseEnter, children: _jsx(HighlightText, { highlight: debounceSearch, children: option.label }) }));
});
export const MarketplaceSelectOption = memo(({ option, size = defaultControlSize, active, hover, disabled, onClick, onMouseEnter, }) => {
    const { debounceSearch } = useContext(USelectContext);
    const flagIcon = flagsMarketplaceRelation[option.icon ||
        option.value];
    return (_jsxs(UltraSelectOption, { "$size": size, "$active": active, "$hover": hover, "$disabled": disabled, role: "option", "data-value": option.value, onClick: onClick, onMouseEnter: onMouseEnter, children: [_jsx(StyledFlagLabel, { children: flagIcon }), _jsx(HighlightText, { highlight: debounceSearch, children: option.label })] }));
});
const StyledFlagLabel = styled(FlagLabel) `
  margin-right: 8px;
`;
export const MultiSelectOption = memo(({ option, size = defaultControlSize, active, hover, disabled, onClick, onMouseEnter, }) => {
    const { debounceSearch } = useContext(USelectContext);
    return (_jsxs(UltraSelectOption, { "$size": size, "$active": active, "$hover": hover, "$disabled": disabled, role: "option", "data-value": option.value, onClick: onClick, onMouseEnter: onMouseEnter, children: [_jsx(UltraSelectSimpleCheckbox, { icon: option.icon, active: active, disabled: disabled }), _jsx(Label, { children: _jsx(HighlightText, { highlight: debounceSearch, children: option.label }) })] }));
});
const selectOptionSizeThemes = {
    "50": css `
    ${cssBody200};
    padding: 4px 8px;
  `,
    "100": css `
    ${cssBody400};
    padding: 8px;
  `,
    "200": css `
    ${cssBody400};
    padding: 8px 12px;
  `,
    "400": css `
    ${cssBody400};
    padding: 8px 12px;
  `,
};
export const UltraSelectOption = styled.div `
  display: flex;
  align-items: center;
  cursor: pointer;

  ${(p) => selectOptionSizeThemes[p.$size || defaultControlSize]};

  ${(p) => p.$active &&
    css `
      background: ${(p) => p.theme.systemColors.grey200};
      border-color: ${(p) => p.theme.systemColors.grey200};

      &:hover {
        background: ${(p) => p.theme.systemColors.grey220};
      }
    `};

  ${(p) => p.$disabled &&
    css `
      opacity: 0.7;
      cursor: not-allowed;
    `};

  ${(p) => !p.$active &&
    !p.$disabled &&
    css `
      &:hover {
        background: ${(p) => p.theme.systemColors.grey100};
      }
    `};

  ${(p) => p.$hover &&
    css `
      background: ${(p) => p.theme.systemColors.grey100};
    `};
`;
const StyledSingleOption = styled(UltraSelectOption) `
  display: block;
  cursor: pointer;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const Label = styled.span `
  display: block;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin-left: 8px;
`;
export const ProductSelectOption = memo(({ option, size = defaultControlSize, active, hover, disabled, onClick, onMouseEnter, getBlocks, }) => {
    return (_jsx(StyledProductOption, { "$size": size, "$active": active, "$hover": hover, "$disabled": disabled, role: "option", "data-value": option.value, onClick: onClick, onMouseEnter: onMouseEnter, children: _jsx(ProductSummary, { market: option.market, product: option, overflowTitleTooltip: false, blocks: getBlocks?.(option), size: "dropdown" }) }));
});
const StyledProductOption = styled(UltraSelectOption) `
  padding: ${(p) => p.theme.spacing.sp8} ${(p) => p.theme.spacing.sp8};
`;
export const MultiProductSelectOption = memo(({ size = defaultControlSize, active, hover, disabled, option, onClick, onMouseEnter, getBlocks, }) => {
    return (_jsxs(StyledProductOption, { "$size": size, "$active": active, "$hover": hover, role: "option", "data-value": option.value, onClick: onClick, onMouseEnter: onMouseEnter, children: [_jsx(UltraSelectSimpleCheckbox, { active: active, disabled: disabled }), _jsx(StyledProduct, { market: option.market, product: option, overflowTitleTooltip: false, blocks: getBlocks?.(option) })] }));
});
export const UltraSelectSimpleCheckbox = ({ active, disabled, icon, className, }) => {
    return (_jsx(NewCheckbox.Box, { "$disabled": disabled, "$active": active, className: className, children: active && _jsx(NewCheckbox.Icon, { icon: icon || faCheck }) }));
};
const StyledProduct = styled(ProductSummary) `
  margin-left: 8px;
`;
